/**
 * Scope-3 Vendor Carbon Footprint (Patent Sub-system W)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, Leaf } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtKg = (n) => `${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} kg`;

const Scope3 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API_URL}/scope3/demo`).then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="scope3-page">
      <Helmet><title>Scope-3 Carbon Footprint | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-lime-300 border border-lime-500/30 rounded-md px-2 py-1 mb-5" data-testid="s3-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Scope-3 Carbon</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">Watershed / Persefoni report scope-3 at the ENTERPRISE level. <span className="text-lime-300">We attribute it per workload.</span></p>
          <p className="mt-3 text-sm text-slate-500">Hardware embodied carbon (instance + GPU + network + storage) + SaaS-vendor service-category emissions (LLM, monitoring, vector DB, feature flags) → per-workload Scope-3 footprint.</p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <>
            <div className="mt-10 grid grid-cols-3 gap-3" data-testid="s3-summary">
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-emerald-300">Scope-2 (grid electricity)</div><div className="text-2xl font-bold text-white mt-1">{fmtKg(data.scope2_kg_co2_per_month)}</div><div className="text-[10px] text-slate-500">covered by Sub-system P</div></CardContent></Card>
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-lime-500/30"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-lime-300">Scope-3 (value chain)</div><div className="text-2xl font-bold text-white mt-1" data-testid="s3-scope3">{fmtKg(data.scope3_total_kg_co2_per_month)}</div><div className="text-[10px] text-slate-500">= {data.scope3_share_pct}% of total</div></CardContent></Card>
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">Total workload</div><div className="text-2xl font-bold text-white mt-1">{fmtKg(data.total_kg_co2_per_month)}</div><div className="text-[10px] text-slate-500">{data.workload_name}</div></CardContent></Card>
            </div>

            <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Leaf className="w-5 h-5 text-lime-300" /> Scope-3 breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2" data-testid="s3-breakdown">
                  {data.scope3_breakdown.map((b, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded border border-white/5 bg-slate-950">
                      <div className="flex-1">
                        <div className="text-sm text-white font-mono">{b.category}</div>
                        <div className="text-xs text-slate-300 mt-1">{b.rationale}</div>
                      </div>
                      <span className="text-lime-300 font-mono font-semibold text-sm whitespace-nowrap">{fmtKg(b.kg_co2_per_month)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Scope3;
